/**
 * InrAmount — parses a currency string from a country_playbook (e.g.
 * "EUR 992/mo", "DKK 3,060", "GBP 1,136-1,483/mo", "~EUR 760/mo",
 * "HUF 185,000/mo", "CHF 21,000/yr") and renders an INR conversion
 * beneath the original.
 *
 * Honest fallback: if the string cannot be parsed OR the currency isn't
 * in the supported set OR the FX rates aren't available yet, this renders
 * nothing. Never guesses, never fabricates.
 *
 * Format:
 *   ≈ ₹92,400/mo · today's rate: €1 = ₹109.88 (as of Jul 21, 2026)
 *
 * For ranges ("EUR 615-877/mo") we convert both bounds:
 *   ≈ ₹67,600 – ₹96,400/mo · today's rate: …
 */
import { useMemo } from "react";
import { useFxInr } from "@/hooks/useFxInr";

const SUPPORTED = new Set(["EUR", "CHF", "NOK", "SEK", "DKK", "PLN", "HUF", "CZK", "GBP"]);

// Currency symbol → ISO code, for playbook strings that use symbols.
const SYMBOL_TO_CODE = { "€": "EUR", "£": "GBP", "kr": "SEK" };

const SYMBOL_FOR = { EUR: "€", CHF: "CHF", NOK: "kr", SEK: "kr", DKK: "kr", PLN: "zł", HUF: "Ft", CZK: "Kč", GBP: "£" };

/**
 * Parse a raw fee/funds string. Returns null if unrecognizable.
 * Shape: { code: "EUR", low: 992, high: 992, period: "mo"|"yr"|null }
 */
export function parseCurrencyString(raw) {
  if (!raw || typeof raw !== "string") return null;
  const s = raw.trim();
  // Try ISO code prefix first (most common in this dataset).
  const codeRe = /(EUR|CHF|NOK|SEK|DKK|PLN|HUF|CZK|GBP|INR|USD)\s*([0-9][0-9,\s]*)(?:\s*[-–—]\s*([0-9][0-9,\s]*))?/i;
  let match = s.match(codeRe);
  let code = null, lowStr = null, highStr = null;
  if (match) {
    code = match[1].toUpperCase();
    lowStr = match[2];
    highStr = match[3] || null;
  } else {
    // Fallback: single-symbol prefix like "€75" or "£1,136-1,483".
    const symRe = /([€£$])\s*([0-9][0-9,\s]*)(?:\s*[-–—]\s*([0-9][0-9,\s]*))?/;
    match = s.match(symRe);
    if (match) {
      code = SYMBOL_TO_CODE[match[1]] || null;
      lowStr = match[2];
      highStr = match[3] || null;
    }
  }
  if (!code) return null;
  const toNum = (str) => {
    const n = parseFloat(String(str).replace(/[,\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const low = toNum(lowStr);
  if (low === null) return null;
  const high = highStr ? toNum(highStr) : low;

  // Detect trailing "/mo" or "/yr" (case-insensitive).
  let period = null;
  if (/\/\s*mo\b/i.test(s) || /\/month\b/i.test(s)) period = "mo";
  else if (/\/\s*yr\b/i.test(s) || /\/year\b/i.test(s)) period = "yr";

  return { code, low, high: high ?? low, period };
}

function _fmtInr(n) {
  // Indian grouping (1,23,456) — a small dignified touch for the audience.
  if (!Number.isFinite(n)) return null;
  return "\u20B9" + Math.round(n).toLocaleString("en-IN");
}

function _fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

export default function InrAmount({ raw, size = "sm", showRateLine = true, className = "" }) {
  const parsed = useMemo(() => parseCurrencyString(raw), [raw]);
  const { rates, asOf, ready, available } = useFxInr();

  if (!parsed) return null;
  if (!SUPPORTED.has(parsed.code)) return null; // INR/USD/etc. Skipped. Nothing to convert.
  if (!ready) return null;                       // don't flash a fake number
  if (!available || !rates?.[parsed.code]) return null;

  const rate = rates[parsed.code];
  const lowInr = parsed.low * rate;
  const highInr = parsed.high * rate;
  const same = Math.round(lowInr) === Math.round(highInr);
  const periodSuffix = parsed.period === "mo" ? "/mo" : parsed.period === "yr" ? "/yr" : "";
  const amountText = same
    ? `${_fmtInr(lowInr)}${periodSuffix}`
    : `${_fmtInr(lowInr)} – ${_fmtInr(highInr)}${periodSuffix}`;

  const rateText = `${SYMBOL_FOR[parsed.code] || parsed.code}1 = ${_fmtInr(rate)}, as of ${_fmtDate(asOf)}`;
  const sizeCls = size === "xs"
    ? "text-[10px] leading-tight"
    : size === "md"
    ? "text-[13px] leading-snug"
    : "text-[11px] leading-tight";

  return (
    <div
      data-testid={`inr-conversion-${parsed.code.toLowerCase()}`}
      className={`mt-1 ${sizeCls} ${className}`}
    >
      <span className="text-elec font-semibold">≈ {amountText}</span>
      {showRateLine && (
        <span className="text-muted"> · today's rate: {rateText}</span>
      )}
    </div>
  );
}
